import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from '../hooks/useAuth';
import { useLang } from '../hooks/useLang';
import './LeaveReview.css';

const SENDER_CATEGORIES = [
  { key: 'punctuality', label: '⏱ Punctuality', desc: 'Was the transporter on time?' },
  { key: 'communication', label: '💬 Communication', desc: 'Was the transporter responsive?' },
  { key: 'packaging_care', label: '📦 Packaging care', desc: 'Were items handled carefully?' },
  { key: 'reliability', label: '🤝 Reliability', desc: 'Did they follow through on commitments?' },
  { key: 'value_for_money', label: '💶 Value for money', desc: 'Was the price fair?' },
];

const TRANSPORTER_CATEGORIES = [
  { key: 'communication', label: '💬 Communication', desc: 'Was the sender responsive?' },
  { key: 'reliability', label: '🤝 Reliability', desc: 'Did they show up on time for pickup?' },
  { key: 'trustworthiness', label: '🔒 Trustworthiness', desc: 'Were the items as declared?' },
  { key: 'item_declaration', label: '📋 Item declaration', desc: 'Did they declare all items accurately?' },
];

function StarInput({ value, onChange }) {
  const [hover, setHover] = useState(0);
  return (
    <div className="star-input">
      {[1, 2, 3, 4, 5].map(star => (
        <button
          key={star}
          type="button"
          className={'star-btn' + (star <= (hover || value) ? ' filled' : '')}
          onMouseEnter={() => setHover(star)}
          onMouseLeave={() => setHover(0)}
          onClick={() => onChange(star)}
        >
          ★
        </button>
      ))}
      <span className="star-label">
        {['', 'Poor', 'Fair', 'Good', 'Very good', 'Excellent'][hover || value] || ''}
      </span>
    </div>
  );
}

export default function LeaveReview() {
  const { bookingId } = useParams();
  const { user, profile } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [alreadyReviewed, setAlreadyReviewed] = useState(false);
  const [error, setError] = useState('');
  const [ratings, setRatings] = useState({
    overall: 0,
    punctuality: 0,
    communication: 0,
    packaging_care: 0,
    reliability: 0,
    value_for_money: 0,
    trustworthiness: 0,
    item_declaration: 0,
  });
  const [comment, setComment] = useState('');

  useEffect(() => { fetchBooking(); }, [bookingId]);

  async function fetchBooking() {
    if (!user) { navigate('/login'); return; }

    const { data: b } = await supabase
      .from('bookings')
      .select('*, trips(origin_city, destination_city, departure_date, transporter_id, profiles!transporter_id(full_name)), profiles!sender_id(full_name)')
      .eq('id', bookingId)
      .single();

    if (!b) { navigate('/dashboard'); return; }
    if (b.status !== 'delivered' && b.status !== 'cancelled') { navigate('/dashboard'); return; }

    // Check if already reviewed
    const { data: existing } = await supabase
      .from('reviews')
      .select('id')
      .eq('booking_id', bookingId)
      .eq('reviewer_id', user.id)
      .maybeSingle();

    if (existing) setAlreadyReviewed(true);
    setBooking(b);
    setLoading(false);
  }

  const isTransporter = booking?.trips?.transporter_id === user?.id;
  const revieweeName = isTransporter ? booking?.profiles?.full_name : booking?.trips?.profiles?.full_name;
  const revieweeId = isTransporter ? booking?.sender_id : booking?.trips?.transporter_id;
  const categories = isTransporter ? TRANSPORTER_CATEGORIES : SENDER_CATEGORIES;

  function setRating(key, val) { setRatings(r => ({ ...r, [key]: val })); }

  async function handleSubmit(e) {
    e.preventDefault();
    if (ratings.overall === 0) { setError('Please give an overall rating.'); return; }
    const missingCat = categories.find(c => ratings[c.key] === 0);
    if (missingCat) { setError(`Please rate ${missingCat.label}.`); return; }

    setSubmitting(true);
    setError('');

    const reviewData = {
      booking_id: bookingId,
      reviewer_id: user.id,
      reviewee_id: revieweeId,
      reviewer_role: isTransporter ? 'transporter' : 'sender',
      rating: ratings.overall,
      comment,
      communication: ratings.communication,
      reliability: ratings.reliability,
      ...(isTransporter ? {
        trustworthiness: ratings.trustworthiness,
        item_declaration: ratings.item_declaration,
      } : {
        punctuality: ratings.punctuality,
        packaging_care: ratings.packaging_care,
        value_for_money: ratings.value_for_money,
      }),
    };

    const { error: err } = await supabase.from('reviews').insert(reviewData);
    if (err) { setError(err.message); setSubmitting(false); return; }
    navigate('/dashboard');
  }

  if (loading) return <div className="loading-screen"><div className="spinner" /></div>;

  if (alreadyReviewed) return (
    <div className="page"><div className="container" style={{ textAlign: 'center', paddingTop: '80px' }}>
      <div style={{ fontSize: '48px' }}>✅</div>
      <h2 style={{ margin: '16px 0 8px' }}>{t('review.alreadyReviewed')}</h2>
      <p style={{ color: 'var(--olive)' }}>{t('review.alreadyReviewedDesc')}</p>
      <button className="btn btn-primary" style={{ marginTop: '24px' }} onClick={() => navigate('/dashboard')}>{t('review.backToDashboard')}</button>
    </div></div>
  );

  return (
    <div className="page review-page">
      <div className="container">
        <div className="review-card card">
          <div className="review-header">
            <div className="review-avatar">{revieweeName?.[0] || '?'}</div>
            <div>
              <h2>Review {revieweeName}</h2>
              <p style={{ color: 'var(--olive)', fontSize: '13px', margin: 0 }}>
                {booking?.trips?.origin_city} → {booking?.trips?.destination_city}
              </p>
            </div>
          </div>

          {error && <div className="alert alert-error">{error}</div>}

          <form onSubmit={handleSubmit}>
            <div className="review-section">
              <h3>{t('review.overallRating')}</h3>
              <StarInput value={ratings.overall} onChange={v => setRating('overall', v)} />
            </div>

            <div className="review-section">
              <h3>{t('review.categoryRatings')}</h3>
              <div className="category-ratings">
                {categories.map(cat => (
                  <div key={cat.key} className="category-row">
                    <div className="category-info">
                      <span className="category-label">{cat.label}</span>
                      <span className="category-desc">{cat.desc}</span>
                    </div>
                    <StarInput value={ratings[cat.key]} onChange={v => setRating(cat.key, v)} />
                  </div>
                ))}
              </div>
            </div>

            <div className="review-section">
              <h3>Comment <span style={{ color: 'var(--olive)', fontWeight: '400', fontSize: '13px' }}>(optional)</span></h3>
              <textarea
                placeholder={isTransporter ? t('review.transporterPlaceholder') : t('review.senderPlaceholder')}
                rows={4}
                value={comment}
                onChange={e => setComment(e.target.value)}
                style={{ width: '100%', marginTop: '8px' }}
              />
            </div>

            <div className="review-actions">
              <button type="button" className="btn btn-ghost" onClick={() => navigate('/dashboard')}>Cancel</button>
              <button type="submit" className="btn btn-primary" disabled={submitting}>
                {submitting ? t('review.submittingBtn') : t('review.submitBtn')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}